// Unified damage map: shows a default SVG diagram (car/motorbike) OR a
// custom uploaded image, and overlays clickable %-based damage markers.
// Markers are stored as x_pct / y_pct so they stay accurate on any screen.

import { useRef } from "react";
import { MotorbikeDiagram } from "./MotorbikeDiagram";

const STROKE = "#0f172a";
const STROKE_W = 0.5;
const FILL = "#ffffff";

/* ─── Default Car SVG views (refined from earlier CarDiagram) ─────────── */

function CarFront() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round" strokeLinecap="round">
      <path d="M20 50 Q 22 32 30 28 L70 28 Q 78 32 80 50 L80 53 L20 53 Z" />
      <path d="M32 30 Q 36 22 50 21 Q 64 22 68 30 Z" />
      <path d="M30 28 Q 50 18 70 28" />
      <line x1="22" y1="45" x2="78" y2="45" />
      <ellipse cx="28" cy="42" rx="4" ry="2" />
      <ellipse cx="72" cy="42" rx="4" ry="2" />
      <rect x="42" y="40" width="16" height="6" rx="1" />
      <line x1="18" y1="51" x2="82" y2="51" />
      <circle cx="26" cy="53" r="3" fill={STROKE} />
      <circle cx="74" cy="53" r="3" fill={STROKE} />
    </g>
  );
}
function CarBack() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round">
      <path d="M20 50 Q 22 32 28 28 L72 28 Q 78 32 80 50 L80 53 L20 53 Z" />
      <path d="M30 30 L 70 30 L 66 22 L 34 22 Z" />
      <path d="M28 28 L 30 30 M 72 28 L 70 30" />
      <rect x="22" y="38" width="10" height="5" rx="1" />
      <rect x="68" y="38" width="10" height="5" rx="1" />
      <rect x="40" y="44" width="20" height="6" rx="1" />
      <line x1="18" y1="51" x2="82" y2="51" />
      <circle cx="26" cy="53" r="3" fill={STROKE} />
      <circle cx="74" cy="53" r="3" fill={STROKE} />
    </g>
  );
}
function CarSide({ flip = false }) {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round"
       transform={flip ? "translate(100 0) scale(-1 1)" : undefined}>
      <path d="M8 48 L 12 38 Q 22 28 40 26 L 62 26 Q 76 28 86 36 L 92 38 L 92 48 L 8 48 Z" />
      <path d="M22 28 Q 40 16 62 22 L 70 26" />
      <path d="M32 28 L 32 46 M 32 28 L 38 22" />
      <line x1="52" y1="28" x2="52" y2="46" />
      <line x1="40" y1="22" x2="40" y2="28" />
      <line x1="60" y1="22" x2="60" y2="28" />
      <line x1="36" y1="36" x2="42" y2="36" />
      <line x1="56" y1="36" x2="62" y2="36" />
      <path d="M28 30 l -3 -2" />
      <circle cx="24" cy="50" r="5" />
      <circle cx="76" cy="50" r="5" />
      <circle cx="24" cy="50" r="2" fill={STROKE} />
      <circle cx="76" cy="50" r="2" fill={STROKE} />
    </g>
  );
}
function CarTop() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round">
      <rect x="18" y="14" width="64" height="32" rx="6" />
      <line x1="18" y1="22" x2="82" y2="22" />
      <line x1="18" y1="38" x2="82" y2="38" />
      <rect x="30" y="22" width="40" height="16" rx="2" />
      <rect x="14" y="24" width="4" height="4" />
      <rect x="82" y="24" width="4" height="4" />
      <line x1="30" y1="22" x2="30" y2="38" />
      <line x1="50" y1="22" x2="50" y2="38" />
      <line x1="70" y1="22" x2="70" y2="38" />
      <rect x="22" y="13" width="6" height="4" rx="1" />
      <rect x="72" y="13" width="6" height="4" rx="1" />
      <rect x="22" y="43" width="6" height="4" rx="1" />
      <rect x="72" y="43" width="6" height="4" rx="1" />
    </g>
  );
}
function CarInterior() {
  return (
    <g fill={FILL} stroke={STROKE} strokeWidth={STROKE_W} strokeLinejoin="round">
      <path d="M14 20 L 86 20 L 80 30 L 20 30 Z" />
      <circle cx="30" cy="24" r="4" />
      <circle cx="30" cy="24" r="1.4" fill={STROKE} />
      <rect x="46" y="22" width="14" height="6" rx="0.5" />
      <rect x="18" y="32" width="20" height="14" rx="2" />
      <rect x="62" y="32" width="20" height="14" rx="2" />
      <rect x="44" y="32" width="12" height="14" rx="1" />
      <rect x="16" y="48" width="68" height="6" rx="2" />
    </g>
  );
}

function CarDiagramSvg({ view }) {
  switch (view) {
    case "front": return <CarFront />;
    case "back": return <CarBack />;
    case "left": return <CarSide flip={false} />;
    case "right": return <CarSide flip={true} />;
    case "top": return <CarTop />;
    case "interior": return <CarInterior />;
    default: return <CarFront />;
  }
}

/* ─── Unified damage map component ────────────────────────────────────── */

export function VehicleDamageMap({
  vehicleType = "car",           // 'car' | 'motorbike'
  view = "front",                 // built-in view OR ignored if customImage is set
  customImage = null,             // { id, content_type, image_data } from /damage-maps
  damages = [],
  selectedId = null,
  onSelect,
  onClickEmpty,
  readOnly = false,
}) {
  const containerRef = useRef(null);

  const handleClick = (e) => {
    if (readOnly || !onClickEmpty) return;
    if (e.target.closest("[data-marker]")) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x_pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y_pct = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    // For custom images, set view = `custom:<id>` so we can filter markers per-image.
    const viewId = customImage ? `custom:${customImage.id}` : view;
    onClickEmpty({ x_pct, y_pct, view: viewId });
  };

  const currentViewId = customImage ? `custom:${customImage.id}` : view;
  const visible = damages.filter((d) => d.view === currentViewId);

  // Aspect ratio: 5/3 for SVG views; for uploaded images use 4/3 so portrait
  // images don't squish (object-contain keeps them centred regardless).
  const aspectRatio = customImage ? "4 / 3" : "5 / 3";

  return (
    <div
      ref={containerRef}
      className={`relative w-full max-w-2xl mx-auto ${readOnly ? "" : "cursor-crosshair"}`}
      style={{ aspectRatio }}
      onClick={handleClick}
      data-testid={`vdm-${currentViewId}`}
    >
      {customImage ? (
        <img
          src={`data:${customImage.content_type || "image/jpeg"};base64,${customImage.image_data}`}
          alt={customImage.view_name || "Damage map"}
          className="absolute inset-0 w-full h-full object-contain rounded-md select-none pointer-events-none"
          draggable={false}
        />
      ) : (
        <svg
          viewBox="0 0 100 60"
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 w-full h-full select-none pointer-events-none"
          data-testid={`vdm-svg-${view}`}
        >
          {vehicleType === "motorbike"
            ? <MotorbikeDiagram view={view} />
            : <CarDiagramSvg view={view} />}
        </svg>
      )}

      {/* Damage markers (percentage-positioned for mobile/desktop accuracy) */}
      <div className="absolute inset-0 pointer-events-none">
        {visible.map((d, idx) => {
          const isSelected = d.id === selectedId;
          const severity = d.severity || "small";
          const dot =
            severity === "big" ? "bg-rose-600 ring-rose-300"
            : severity === "medium" ? "bg-orange-500 ring-orange-300"
            : "bg-amber-500 ring-amber-200";
          const repaired = d.status === "repaired";
          return (
            <button
              key={d.id || idx}
              type="button"
              data-marker
              data-testid={`damage-marker-${d.id || idx}`}
              title={`${d.tag_label || d.tag_code} · ${severity}`}
              onClick={(e) => { e.stopPropagation(); onSelect?.(d); }}
              style={{ left: `${d.x_pct}%`, top: `${d.y_pct}%` }}
              className={`pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full text-white text-[10px] font-bold grid place-items-center
                          ring-2 ${dot} ${repaired ? "opacity-40 line-through" : ""}
                          ${isSelected ? "ring-4 ring-emerald-400 scale-110" : ""}
                          shadow-md hover:scale-110 transition-transform`}
            >
              {idx + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}
